import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import BulletedList from '@nmx/template/dist/componentLibrary/components/BulletedList';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';

export default () => (
  <SectionContainer
    id='variable-universal-life-insurance-faq' // '-section-container' will be appended dynamically
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='typography'>
          Questions about variable universal life insurance&#63; <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
    </Row>
    <Row align='center'>
      <Col
        medium={10}
        large={8}>
        <Accordion
          id='faq-variable-universal-life-insurance'
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: 'variable-universal-life-insurance-how-does-it-work-faq',
              title: (<>How does variable universal life <WidowBlock>insurance work&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Each time you pay your premium, expenses are taken out and the remainder goes into your policy&#39;s cash value. Monthly expenses (including the cost of insurance) are deducted. Then your policy&#39;s cash value can be invested in a variety of ways. The performance of your investments is added or subtracted from your policy&#39;s cash value on a daily basis. <Link linkUrl='/life-and-money/how-variable-universal-life-insurance-works/' variant='primaryDeemphasize'>Find out more about how VUL works.</Link>
                </Typography>
              ),
            },
            {
              id: 'variable-universal-life-insurance-pros-and-cons-faq',
              title: (<>What are the pros and cons of variable universal <WidowBlock>life insurance&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography
                    variant='h5'
                    nextElement='typography'>
                    Pros:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: 'VUL gives you lifelong protection for the people you love, along with premium and death benefit flexibility. It also lets you choose how your cash value is invested, so you could have the opportunity to build even more cash value (potentially leading to a higher death benefit) than you could with other permanent life insurance policies.' },
                      { bulletText: 'Its fees are transparent. You can view the expenses and charges that your policy incurs, along with the performance of your investments on your annual policy statement.' },
                      { bulletText: <>It can build cash value that will grow tax deferred and can be used for anything, anytime.<sup>1</sup></> },
                      { bulletText: <>It can be a way to reach other financial goals faster if you&#39;re willing to accept market risk.</> },
                      { bulletText: 'VUL offers a guaranteed death benefit for a certain period of time regardless of market performance as long as the required premiums are paid.' },
                    ]}
                  />
                  <Typography
                    variant='h5'
                    nextElement='typography'>
                    Cons:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: <>VUL might not be right for you if you&#39;re uncomfortable with market risk since your policy&#39;s cash value could fluctuate based on the performance of the underlying investment options you choose. It&#39;s also possible that your policy could lapse if your cash value is ever too low to cover policy costs (unless additional premium payments are made).</> },
                      { bulletText: <>VUL is appropriate for people who have a long time horizon before they&#39;ll need to access their policy&#39;s cash value.</> },
                      { bulletText: 'VUL appropriate for people who are interested in monitoring their policy performance.' },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    Want to find out more&#63;{' '}
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about the pros and cons of variable universal life insurance with a financial advisor'>
                      Connect with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
            {
              id: 'variable-universal-life-insurance-what-happens-to-the-death-benefit-faq',
              title: (<>What happens to my variable universal life death benefit if the market <WidowBlock>goes down&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  When you first get your policy, you can decide how long you want your death benefit to be guaranteed. As long as you continue to pay the premiums required for your death benefit guarantee, your policy will remain in force and the death benefit won&#39;t be subject to market ups and downs. Want to find out more&#63; <Link linkUrl='/find-a-financial-advisor/' variant='primaryDeemphasize' ariaLabel='Learn more with a financial advisor'>Connect with an advisor.</Link>
                </Typography>
              ),
            },
            {
              id: 'variable-universal-life-insurance-good-investment-faq',
              title: (<>Is variable universal life insurance a <WidowBlock>good investment&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Since you have a wide variety of options for how your cash value is invested, your VUL policy has the potential to build even more cash value than it could with whole life insurance. On the other hand, it does have the potential to go down in value as well, since your cash value, and possibly your death benefit, are tied to the performance of those investments. So VUL could be right for you if you are comfortable with higher risk and have a long time horizon for the chance to see higher returns.
                  </Typography>
                  <Typography disableBottomPadding>
                    Unlike products that are taxed annually, VUL lets you build cash value tax deferred, giving you the opportunity for compound growth since the money you don&#39;t pay in taxes can continue to be invested. Keep in mind that the cost of insurance will still need to be paid out of your policy&#39;s cash value. Want to find out more&#63; <Link linkUrl='/find-a-financial-advisor/' variant='primaryDeemphasize' ariaLabel='Learn more about investments with a financial advisor'>Connect with an advisor.</Link>
                  </Typography>
                </>
              ),
            },
            {
              id: 'variable-universal-life-insurance-vul-and-vl-compared-faq',
              title: (<>Is variable universal life insurance (VUL) the same as Variable Life Insurance (VL) and Universal Life <WidowBlock>Insurance (UL)&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    UL and VUL are structured in a similar way&#8212;the difference being how your cash value is invested. With UL, its growth is backed by the company&#39;s general account. For VUL, the policy&#39;s cash value is invested in options chosen by the policyowner, and the cash value (and possibly death benefit) are determined by how well the investments perform.
                  </Typography>
                  <Typography disableBottomPadding>
                    VL is similar to whole life in that it comes with a guaranteed death benefit and set premiums. But the policy&#39;s cash value growth and death benefit are not back by the company&#39;s general account. Instead, the policyowner chooses from a variety of investment options. VUL, on the other hand, the policy&#39;s cash value grows in much the same way, but also gives you flexibility in your premiums and death benefit amount. <Link linkUrl='/life-and-money/life-insurance-guide/' variant='primaryDeemphasize'>See our guide to life insurance.</Link>
                  </Typography>
                </>
              ),
            },
            {
              id: 'variable-universal-life-insurance-who-can-benefit-faq',
              title: (<>Who can benefit most from variable <WidowBlock>universal life&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  VUL could work well for someone who wants the lifelong coverage you get with permanent life insurance, as well as the possibility of growing their cash value faster than they could with a whole life policy. It could also be right for someone who wants control over how their cash value is invested and the option to reallocate cash value over time, as well as someone who can take on market risk for the potential to see higher returns. Want to find out more&#63; <Link linkUrl='/find-a-financial-advisor/' variant='primaryDeemphasize' ariaLabel='Learn more about variable universal life insurance benefits with a financial advisor'>Connect with an advisor.</Link>
                </Typography>
              ),
            },
            {
              id: 'variable-universal-life-insurance-vul-and-whole-life-compared-faq',
              title: (<>What are the differences between variable universal life and <WidowBlock>whole life&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography
                    variant='h5'
                    nextElement='typography'>
                    How they&#39;re similar:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      { bulletText: 'Both are types of permanent life insurance.' },
                      { bulletText: 'Both build cash value.' },
                    ]}
                  />
                  <Typography
                    variant='h5'
                    nextElement='typography'>
                    How they&#39;re different:
                  </Typography>
                  <BulletedList
                    bulletType='filledCircle'
                    bulletedListData={[
                      {
                        bulletText: (
                          <Typography disableBottomPadding>
                            <Link
                              linkUrl='/life-insurance/whole-life-insurance/'
                              variant='primaryDeemphasize'
                              ariaLabel='Whole life insurance'>
                              Whole life
                            </Link>
                            {' '}doesn&#39;t let you invest your policy&#39;s cash value, while universal life does.
                          </Typography>
                        ),
                      },
                      {
                        bulletText: (
                          <Typography disableBottomPadding>
                            <Link
                              linkUrl='/life-insurance/whole-life-insurance/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about whole life insurance'>
                              Whole life
                            </Link>
                            {' '}comes with a guaranteed death benefit, while universal life comes with a flexible death benefit.
                          </Typography>
                        ),
                      },
                      {
                        bulletText: (
                          <Typography disableBottomPadding>
                            <Link
                              linkUrl='/life-insurance/whole-life-insurance/'
                              variant='primaryDeemphasize'
                              ariaLabel='Learn more about premiums for whole life insurance'>
                              Whole life
                            </Link>
                            {' '}comes with set premiums, while universal life comes with flexible premiums.
                          </Typography>
                        ),
                      },
                    ]}
                  />
                  <Typography disableBottomPadding>
                    <Link
                      linkUrl='/life-and-money/life-insurance-guide/'
                      ariaLabel='Learn more about life insurance'
                      variant='primaryDeemphasize'>
                      See our guide to life insurance.
                    </Link>
                  </Typography>
                </>
              ),
            },
            {
              id: 'variable-universal-life-insurance-investment-options-faq',
              title: (<>What are the investment options in Northwestern Mutual&#39;s variable universal life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    Northwestern Mutual offers 40 investment funds you can choose from when you own a Northwestern Mutual Select variable universal life policy. Each fund has its own focus, investment goals, and investment advisor(s). You&#39;ll have the option to separately select individual investment funds, or you can choose from a variety of pre&#45;packaged solutions, each designed to work with a particular asset allocation model. These solutions can simplify the investment option selection process for you, since they come with a diversified mix of assets which could make sense for your risk tolerance and financial goals.
                  </Typography>
                  <Typography disableBottomPadding>
                    We also offer access to our Strength and Stability (SAS) account, which can reduce the amount of risk you take on. This unique feature gives you a fixed account option backed by our general account investment portfolio, allowing you to de&#45;risk your VUL policy over time. You&#39;ll have the option to transfer 5&#45;50&#37; of your cash value (depending on how long you&#39;ve owned your policy) to the SAS account, while the rest remains invested in the market. You might want to take advantage of the upside potential of investing more aggressively in the early years of your policy, and then minimize your risk over time or as things change later on in your life. Want to find out more&#63;{' '}
                    <Link
                      linkUrl='/find-a-financial-advisor/'
                      variant='primaryDeemphasize'
                      ariaLabel='Learn more about investment options with a financial advisor'>
                      Connect with an advisor
                    </Link>.
                  </Typography>
                </>
              ),
            },
          ]}
        />
      </Col>
    </Row>
  </SectionContainer>
);
